import { useState, useRef, type FC } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { StaticTimePicker } from "@mui/x-date-pickers/StaticTimePicker";
import { PickersLayout } from "@mui/x-date-pickers/PickersLayout";
import type { PickersLayoutProps } from "@mui/x-date-pickers/PickersLayout";
import type { DateView } from "@mui/x-date-pickers/models";
import { Popover } from "@mui/material";
import { useT } from "@transifex/react";
import moment from "moment";
import type { Moment } from "moment";
import PhaseSixIcon from "../phaseSixIcon/PhaseSixIcon";
import useBreakpoints, { Breakpoint } from "../../hooks/useBreakpoints";

interface DateTimeInputProps {
    value: Moment | null;
    onChange: (value: Moment | null) => void;
    minDate?: Moment;
    maxDate?: Moment;
    minTime?: Moment;
    maxTime?: Moment;
    infoText?: string;
}

const DATE_FORMAT = "DD.MM.YYYY";
const TIME_FORMAT = "HH:mm";

const PickerInfoBanner = (props: { text: string }) => {
    const sentences = props.text.split(/\.\s+/).map((s, i, arr) => (i < arr.length - 1 ? `${s}.` : s));
    return (
        <div className="w-0 min-w-full p-[24px]">
            <div className="flex flex-1 items-center gap-[16px] border-l-4 border-p6-orange px-[16px] min-w-0">
                <PhaseSixIcon
                    name="info"
                    className="text-[30px] text-p6-grey-dark shrink-0"
                />
                <div className="flex flex-col text-m leading-normal text-p6-grey-dark break-words">
                    {sentences.map((sentence, i) => (
                        <p key={i}>{sentence}</p>
                    ))}
                </div>
            </div>
        </div>
    );
};

const isSameDay = (a: Moment | null | undefined, b: Moment | null | undefined): boolean =>
    !!a && !!b && a.isSame(b, "day");

const DateTimeInput = (props: DateTimeInputProps) => {
    const t = useT();
    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    const t_selectDate = t("Select date", { _tags: "ClassActivity, ScreenReaderOnly" });
    const t_selectTime = t("Select time", { _tags: "ClassActivity, ScreenReaderOnly" });

    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [isTimePickerOpen, setIsTimePickerOpen] = useState(false);
    const dateButtonRef = useRef<HTMLButtonElement>(null);
    const timeButtonRef = useRef<HTMLButtonElement>(null);

    const displayDate = props.value ? props.value.format(DATE_FORMAT) : t_selectDate;
    const displayTime = props.value ? props.value.format(TIME_FORMAT) : t_selectTime;

    // Time bounds only apply when the selected day equals the bounding date.
    const effectiveMinTime = props.minTime && isSameDay(props.value, props.minTime) ? props.minTime : undefined;
    const effectiveMaxTime = props.maxTime && isSameDay(props.value, props.maxTime) ? props.maxTime : undefined;

    const clampToBounds = (next: Moment): Moment => {
        if (props.minTime && isSameDay(next, props.minTime) && next.isBefore(props.minTime)) {
            return next.clone().hour(props.minTime.hour()).minute(props.minTime.minute());
        }
        if (props.maxTime && isSameDay(next, props.maxTime) && next.isAfter(props.maxTime)) {
            return next.clone().hour(props.maxTime.hour()).minute(props.maxTime.minute());
        }
        return next;
    };

    /** Emits the clamped value at minute precision; a value seeded from `moment()` would carry the click's seconds. */
    const emitChange = (next: Moment) => {
        props.onChange(clampToBounds(next).startOf("minute"));
    };

    const handleDateChange = (newDate: Moment | null) => {
        if (newDate && newDate.isValid()) {
            const merged = newDate.clone();
            if (props.value) {
                merged.hour(props.value.hour()).minute(props.value.minute());
            }
            emitChange(merged);
        } else {
            props.onChange(null);
        }
        setIsDatePickerOpen(false);
    };

    const handleTimeChange = (newTime: Moment | null) => {
        if (newTime && newTime.isValid()) {
            const updatedValue = props.value ? props.value.clone() : moment();
            updatedValue.hour(newTime.hour());
            updatedValue.minute(newTime.minute());
            emitChange(updatedValue);
        }
    };

    const handleDateButtonClick = () => {
        setIsDatePickerOpen(true);
    };

    const handleTimeButtonClick = () => {
        setIsTimePickerOpen(true);
    };

    const renderLayoutWithInfo = (layoutProps: PickersLayoutProps<Moment | null, Moment, DateView>) => {
        // PickersLayout generic erases when spread; cast keeps the runtime contract intact.
        const Layout = PickersLayout as unknown as FC<typeof layoutProps>;
        return (
            <div className="flex flex-col bg-white">
                <Layout {...layoutProps} />
                {props.infoText && <PickerInfoBanner text={props.infoText} />}
            </div>
        );
    };

    return (
        <div
            className={`flex items-stretch w-full border border-p6-grey-dark rounded-small-container min-h-[50px] ${getStyleBasedOnBreakpoint(
                Breakpoint.MD,
                "flex-row",
                "flex-col"
            )}`}
        >
            <DatePicker
                open={isDatePickerOpen}
                onClose={() => setIsDatePickerOpen(false)}
                value={props.value}
                onChange={handleDateChange}
                displayWeekNumber
                minDate={props.minDate}
                maxDate={props.maxDate}
                slots={props.infoText ? { layout: renderLayoutWithInfo } : undefined}
                slotProps={{
                    textField: {
                        sx: {
                            position: "absolute",
                            width: 0,
                            height: 0,
                            overflow: "hidden",
                            opacity: 0,
                            pointerEvents: "none",
                        },
                    },
                    popper: {
                        anchorEl: dateButtonRef.current,
                        placement: "bottom-start",
                        modifiers: [
                            {
                                name: "flip",
                                enabled: true,
                                options: {
                                    fallbackPlacements: [
                                        "top-start",
                                        "right-start",
                                        "right-end",
                                        "left-start",
                                        "left-end",
                                        "bottom-end",
                                        "top-end",
                                    ],
                                    rootBoundary: "viewport",
                                },
                            },
                            {
                                name: "preventOverflow",
                                enabled: true,
                                options: { rootBoundary: "viewport", padding: 8 },
                            },
                        ],
                    },
                }}
            />
            <button
                ref={dateButtonRef}
                type="button"
                data-testid="date-time-input-date-button"
                onClick={handleDateButtonClick}
                aria-label={t_selectDate}
                className={`flex flex-1 items-center justify-center gap-s px-xs min-h-[50px] cursor-pointer hover:bg-background-light ${getStyleBasedOnBreakpoint(
                    Breakpoint.MD,
                    "border-r border-p6-grey-mid rounded-l-small-container",
                    "border-b border-p6-grey-mid rounded-t-small-container"
                )}`}
            >
                <PhaseSixIcon
                    name="calendar-cross"
                    className="text-[30px] text-p6-grey-dark"
                />
                <span className="text-m text-p6-grey-dark">{displayDate}</span>
            </button>
            <button
                ref={timeButtonRef}
                type="button"
                onClick={handleTimeButtonClick}
                aria-label={t_selectTime}
                className={`flex flex-1 items-center justify-center gap-s px-xs min-h-[50px] cursor-pointer hover:bg-background-light ${getStyleBasedOnBreakpoint(
                    Breakpoint.MD,
                    "rounded-r-small-container",
                    "rounded-b-small-container"
                )}`}
            >
                <PhaseSixIcon
                    name="clock"
                    className="text-[30px] text-p6-grey-dark"
                />
                <span className="text-m text-p6-grey-dark">{displayTime}</span>
            </button>
            <Popover
                open={isTimePickerOpen}
                anchorEl={timeButtonRef.current}
                onClose={() => setIsTimePickerOpen(false)}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                disableScrollLock
            >
                <div className="flex flex-col bg-white">
                    <StaticTimePicker
                        orientation="portrait"
                        value={props.value}
                        onChange={handleTimeChange}
                        onAccept={() => setIsTimePickerOpen(false)}
                        onClose={() => setIsTimePickerOpen(false)}
                        ampm={false}
                        minTime={effectiveMinTime}
                        maxTime={effectiveMaxTime}
                        slotProps={{ actionBar: { actions: [] } }}
                    />
                    {props.infoText && <PickerInfoBanner text={props.infoText} />}
                </div>
            </Popover>
        </div>
    );
};

export default DateTimeInput;
